import { DateTime } from 'luxon';

import { formatConversionValue, formatCount } from './segmentPerformance';

// The interval/measure values the cohorts endpoint accepts, with the labels the
// selects show. Anything else 400s, so the options are the whole allowed set.
export const COHORT_INTERVALS = [
  { value: 'month', label: 'Monthly' },
  { value: 'week', label: 'Weekly' },
];

export const COHORT_MEASURES = [
  { value: 'profiles', label: 'Profiles' },
  { value: 'orders', label: 'Orders' },
  { value: 'revenue', label: 'Revenue' },
];

// Sequential ramps taken from theme.jsx. `primary` 0-9 carries the intensity of
// a normal cell; negative cells (refunds netted into revenue) get the light half
// of `danger` instead, so a refunded period can never read as a large positive.
// White text starts at primary-6 (#7B3EE0), the first shade dark enough that
// the default near-black text drops under 4.5:1 - every danger shade used here
// stays light enough to keep it.
const POSITIVE_SHADES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const NEGATIVE_SHADES = [1, 3, 5];
const FIRST_LIGHT_TEXT_SHADE = 6;

const PERIOD_UNIT = { month: 'mo', week: 'wk' };

/**
 * A cell's value divided by its cohort's acquired population, or `null` when
 * there is no population to divide by - a 0-size cohort would otherwise
 * produce Infinity. This is a genuine percentage only for `profiles` (a share
 * of the cohort); for `orders`/`revenue` it is a per-customer average, and
 * `formatCohortCellValue` - not this function - is what decides which of the
 * two a given measure gets shown as.
 * @param {number} value
 * @param {number|null} [size]
 * @returns {number|null}
 */
const ratioOfSize = (value, size) => (size ? value / size : null);

/**
 * Display label for a cohort. The API sends `YYYY-MM` for month intervals and
 * the Monday's `YYYY-MM-DD` for week intervals; an unrecognised label is shown
 * as it arrived rather than as Luxon's "Invalid DateTime".
 * @param {string} cohort - raw `cohort` value from the response
 * @param {'month'|'week'} interval
 * @returns {string}
 */
export const formatCohortLabel = (cohort, interval) => {
  const date = DateTime.fromISO(cohort);
  if (!date.isValid) {
    return cohort;
  }
  return interval === 'week' ? date.toFormat('d LLL yyyy') : date.toFormat('LLL yyyy');
};

/**
 * Header for a period column: how much time has elapsed since acquisition.
 * @param {number} index - 0-based period offset
 * @param {'month'|'week'} interval
 * @returns {string} e.g. "0 mo", "3 wk"
 */
export const periodColumnLabel = (index, interval) => `${index} ${PERIOD_UNIT[interval]}`;

/**
 * Shape a cohorts response into the rows of the retention grid.
 *
 * `periods` is triangular: a period a cohort has not lived through yet is
 * absent from the array, which is a different fact from a period that elapsed
 * with no activity (a real 0). Absent periods come back as `null` cells so the
 * grid can leave them blank instead of padding the row with zeroes.
 *
 * A `%`-view cell is the period's value over the cohort's own `size` - the
 * acquired population the API reports, never `periods[0]`. That ratio is a
 * genuine percentage only for `profiles`; `formatCohortCellValue` renders it
 * as a per-customer average for `orders`/`revenue` instead of misreporting a
 * headcount ratio as `%`. The totals row divides each column by the summed
 * size of the cohorts that actually reached it: `totals.size` is the whole
 * population, and a column only the older cohorts have reached would slope
 * towards zero if measured against it.
 *
 * @param {object} [analysis] - cohorts response body
 * @param {boolean} [percent] - true for the `%` view, false for absolute values
 * @returns {{periodCount: number, rows: Array<{cohort: string, size: number, cells: Array<number|null>}>, totals: {size: number, cells: Array<number|null>}|null}}
 */
export const buildCohortMatrix = (analysis, percent) => {
  const cohorts = analysis?.cohorts || [];
  const periodCount = analysis?.maxPeriods || 0;

  const rows = cohorts.map(({ cohort, size, periods }) => {
    // `periods` is the one field elsewhere read without a guard; every other
    // access on this response goes through `?.` or `??`.
    const cohortPeriods = periods || [];
    return {
      cohort,
      size,
      cells: Array.from({ length: periodCount }, (_, index) => {
        if (index >= cohortPeriods.length) {
          return null;
        }
        return percent ? ratioOfSize(cohortPeriods[index], size) : cohortPeriods[index];
      }),
    };
  });

  if (rows.length === 0) {
    return { periodCount, rows, totals: null };
  }

  const totalPeriods = analysis.totals?.periods || [];
  const totals = {
    size: analysis.totals?.size ?? null,
    cells: Array.from({ length: periodCount }, (_, index) => {
      if (index >= totalPeriods.length) {
        return null;
      }
      if (!percent) {
        return totalPeriods[index];
      }
      const reachedSize = cohorts
        .filter((cohort) => (cohort.periods || []).length > index)
        .reduce((sum, cohort) => sum + cohort.size, 0);
      return ratioOfSize(totalPeriods[index], reachedSize);
    }),
  };

  return { periodCount, rows, totals };
};

/**
 * The range the colour scale is keyed to, over the cohort cells only - the
 * totals row is an order of magnitude larger and would flatten every cohort
 * cell to the lightest shade. Anchored at 0 in both directions so that a table
 * with no negative cell has no negative end to its scale.
 * @param {Array<{cells: Array<number|null>}>} rows - `rows` from buildCohortMatrix
 * @returns {{min: number, max: number}}
 */
export const cohortValueExtent = (rows) => {
  let min = 0;
  let max = 0;
  for (const row of rows) {
    for (const value of row.cells) {
      if (value == null) {
        continue;
      }
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  }
  return { min, max };
};

/**
 * Heatmap style for one cell, bucketed by how close its value is to the extreme
 * in its own direction. An absent cell gets no style at all, so it stays visibly
 * empty next to a zero cell - which does get a background, the lightest shade of
 * the ramp.
 * @param {number|null} value - a cell from buildCohortMatrix
 * @param {{min: number, max: number}} extent - from cohortValueExtent
 * @returns {{backgroundColor: string, color?: string}|null} `null` for an absent cell
 */
export const cohortCellStyle = (value, extent) => {
  if (value == null) {
    return null;
  }

  const negative = value < 0;
  const shades = negative ? NEGATIVE_SHADES : POSITIVE_SHADES;
  const peak = Math.abs(negative ? extent.min : extent.max);
  const bucket = peak ? Math.min(shades.length - 1, Math.floor((Math.abs(value) / peak) * shades.length)) : 0;
  const shade = shades[bucket];

  return {
    backgroundColor: `var(--mantine-color-${negative ? 'danger' : 'primary'}-${shade})`,
    color: !negative && shade >= FIRST_LIGHT_TEXT_SHADE ? 'var(--mantine-color-white)' : undefined,
  };
};

/**
 * Text for one cell. An absent period is the empty string: no value, no zero,
 * no "0%".
 *
 * In the `%` view, `value` is always `raw / cohort.size` (see `ratioOfSize`),
 * but only `profiles` is a share of the cohort - `orders`/`revenue` divide a
 * count by a headcount, giving a per-customer average, not a percentage. With
 * `measure: 'revenue'` a cell like `€12,000 / 1,000 profiles` renders as
 * `€12.00`, not `1200.0%`; `orders` renders the same ratio as a plain number.
 * @param {number|null} value - a cell from buildCohortMatrix
 * @param {object} options
 * @param {'profiles'|'orders'|'revenue'} options.measure
 * @param {boolean} [options.percent]
 * @param {string} [options.currency] - ISO code from `state.domain.domain.currency`
 * @returns {string}
 */
export const formatCohortCellValue = (value, { measure, percent, currency }) => {
  if (value == null) {
    return '';
  }
  if (percent) {
    if (measure === 'profiles') {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (measure === 'revenue') {
      return formatConversionValue(value, currency);
    }
    return value.toLocaleString(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return measure === 'revenue' ? formatConversionValue(value, currency) : formatCount(value);
};
